import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Lock } from "lucide-react";
import ScoreBadge from "@/components/ScoreBadge";
import { ptMeta } from "@/lib/propertyTypes";
import { Button } from "@/components/ui/button";
import { useBilling } from "@/context/BillingContext";
import api from "@/lib/api";

function DealCard({ kind, item }) {
  if (!item) return null;
  const to = kind === "vehicle" ? `/vehicles/${encodeURIComponent(item.id)}` : `/homes/${encodeURIComponent(item.id)}`;
  const title = kind === "vehicle" ? (item.heading || `${item.year || ""} ${item.make || ""} ${item.model || ""}`.trim()) : item.address;
  const sub = kind === "vehicle" ? [item.year, item.make].filter(Boolean).join(" · ") : [item.city, item.state].filter(Boolean).join(", ");
  const photo = (item.photos || [])[0];
  const pt = ptMeta(item.property_type);
  const PtIcon = pt.icon;
  const homeFallback = kind !== "vehicle" && (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2" data-testid="deal-home-type-icon">
      <div className={`w-14 h-14 rounded-full flex items-center justify-center border ${pt.cls}`}>
        <PtIcon className="w-7 h-7" strokeWidth={1.5} />
      </div>
      <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{item.property_type || "Home"}</span>
    </div>
  );
  return (
    <Link to={to} data-testid={`deal-${kind}`} className="group relative overflow-hidden bg-surface border border-accent/30 rounded-2xl hover:border-accent/60 transition-colors">
      <div className="relative h-44 bg-secondary/40">
        {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : (homeFallback || <div className="w-full h-full flex items-center justify-center font-mono-data text-xs text-muted-foreground">Verified · no photo</div>)}
        <div className="absolute top-3 left-3 font-mono-data text-[10px] uppercase tracking-wider bg-accent text-accent-foreground rounded-full px-2.5 py-1 flex items-center gap-1"><Sparkles className="w-3 h-3" />{kind === "vehicle" ? "Vehicle" : "Home"} deal</div>
        <div className="absolute top-3 right-3"><ScoreBadge data={{ investment_score: item._score, verdict: item._verdict, band: item._band }} className="backdrop-blur-sm" /></div>
      </div>
      <div className="p-5">
        <div className="font-mono-data text-2xl">{item.price ? `$${Number(item.price).toLocaleString()}` : "—"}</div>
        <div className="font-editorial text-lg italic mt-1 line-clamp-1">{title}</div>
        <div className="font-mono-data text-[11px] text-muted-foreground mt-1">{sub} · {item._verdict} · {item._band} band</div>
        <div className="mt-3 inline-flex items-center gap-1 font-mono-data text-[11px] uppercase tracking-wider text-accent">View listing<ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" /></div>
      </div>
    </Link>
  );
}

export default function DealOfTheDay() {
  const { ent } = useBilling();
  const premium = !!ent?.premium;
  const [deals, setDeals] = useState(null);
  useEffect(() => {
    if (!premium) return;
    api.get("/deals/today").then((r) => {
      const d = r.data || {};
      setDeals(d);
      const sig = `${d.date}|${d.property?.id || ""}|${d.vehicle?.id || ""}`;
      localStorage.setItem("luxora_seen_deal", sig);
      window.dispatchEvent(new Event("luxora-deal-seen"));
    }).catch(() => setDeals(null));
  }, [premium]);

  // Free tier: show that a Deal of the Day exists but withhold it behind a lock.
  if (!premium) {
    return (
      <div className="mb-12" data-testid="deal-of-the-day-locked">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-accent" strokeWidth={1.5} />
          <h2 className="font-display text-2xl font-medium">Deal of the Day</h2>
          <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground ml-1">Highest verified investment score</span>
        </div>
        <div className="relative overflow-hidden rounded-2xl">
          <div className="grid md:grid-cols-2 gap-5 pointer-events-none select-none blur-[7px] opacity-50" aria-hidden>
            {[0, 1].map((i) => (
              <div key={i} className="bg-surface border border-accent/30 rounded-2xl h-64">
                <div className="h-44 bg-secondary/40" />
                <div className="p-5"><div className="font-mono-data text-2xl">$0,000,000</div><div className="font-editorial text-lg italic mt-1">Verified luxury deal</div></div>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4 bg-background/20">
            <div className="w-12 h-12 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center"><Lock className="w-5 h-5 text-accent" /></div>
            <p className="font-mono-data text-[11px] uppercase tracking-wider text-foreground/80">Upgrade to Vaelith Membership to unlock this feature</p>
            <Link to="/pricing"><Button size="sm" className="rounded-full mt-1" data-testid="deal-upgrade"><Sparkles className="w-3.5 h-3.5 mr-1.5" />Upgrade</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  if (!deals?.property && !deals?.vehicle) return null;
  return (
    <div className="mb-12" data-testid="deal-of-the-day">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-accent" strokeWidth={1.5} />
        <h2 className="font-display text-2xl font-medium">Deal of the Day</h2>
        <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground ml-1">Highest verified investment score</span>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <DealCard kind="home" item={deals.property} />
        <DealCard kind="vehicle" item={deals.vehicle} />
      </div>
    </div>
  );
}
